<template>
  <NuxtLink :to="to" class="gr-listcard">
    <div class="gr-listcard__media">
      <img
        v-if="hasBanner && !hasImageError"
        :ref="setImageElement"
        :src="contentImg"
        :alt="contentTitle"
        class="gr-listcard__img"
        loading="lazy"
        decoding="async"
        @error="hasImageError = true"
      />
      <!-- A missing banner and a broken one land on the same neutral stand-in,
           and both drop the alt text to "" because the stand-in says nothing
           about the activity. -->
      <div v-else class="gr-listcard__img gr-listcard__img--fallback">
        <v-icon icon="mdi-image-off-outline" size="26" aria-hidden="true" />
      </div>
    </div>

    <div class="gr-listcard__body">
      <!-- Canonical puts the date above the title for open activities and
           below it for finished ones. Both orders are kept: for an open
           activity "when" is what a visitor decides on, and spec 001
           criterion 16 requires the finished card to read title first. -->
      <template v-if="isFinished">
        <component :is="titleTag" class="gr-listcard__title">
          {{ contentTitle }}
        </component>
        <p class="gr-listcard__date">{{ contentDate }}</p>
      </template>
      <template v-else>
        <p class="gr-listcard__date gr-listcard__date--lead">
          {{ contentDate }}
        </p>
        <component :is="titleTag" class="gr-listcard__title">
          {{ contentTitle }}
        </component>
      </template>

      <div class="gr-listcard__meta">
        <span
          class="gr-listcard__status"
          :class="`gr-listcard__status--${statusMarker.tone}`"
        >
          <v-icon :icon="statusMarker.icon" size="14" aria-hidden="true" />
          {{ statusMarker.text }}
        </span>
        <span class="gr-listcard__city">
          <v-icon icon="mdi-map-marker" size="14" aria-hidden="true" />
          {{ contentCity }}
        </span>
      </div>
    </div>
  </NuxtLink>
</template>

<script lang="ts" setup>
import { ACTIVITY_STATUS_LABEL } from "@/common/utils/activityStatus";
import type { ActivityStatus } from "@/common/utils/activityStatus";

interface ActivityListCardProps {
  to: string;
  contentImg: string;
  contentTitle: string;
  contentDate: string;
  contentRegistrationEndDate: string;
  contentCity: string;
  variant?: "default" | "finished";
  /** Card title depth, so the page's heading ladder never skips a level. */
  headingLevel?: 2 | 3;
  contentStatus?: ActivityStatus;
}

const props = withDefaults(defineProps<ActivityListCardProps>(), {
  variant: "default",
  headingLevel: 3,
  contentStatus: "FINISHED",
});

type StatusMarker = {
  text: string;
  tone: "muted" | "warn" | "danger";
  icon: string;
};

const hasImageError = ref(false);
const hasBanner = computed(() => Boolean(props.contentImg));
const isFinished = computed(() => props.variant === "finished");
const titleTag = computed(() => `h${props.headingLevel}`);

const registrationEndEstimate = computed(() => {
  const endDate = new Date(props.contentRegistrationEndDate);
  const today = new Date();
  endDate.setHours(23, 59, 59, 999);
  today.setHours(0, 0, 0, 0);
  return Math.floor((endDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
});

const registrationEndStatus = computed<StatusMarker>(() => {
  const days = registrationEndEstimate.value;

  if (days < 0)
    return {
      text: "Pendaftaran ditutup",
      tone: "danger",
      icon: "mdi-calendar-remove",
    };
  if (days === 0)
    return { text: "Hari terakhir", tone: "danger", icon: "mdi-clock-alert" };
  if (days === 1)
    return {
      text: "1 hari lagi",
      tone: "warn",
      icon: "mdi-clock-alert-outline",
    };
  if (days <= 3)
    return {
      text: `${days} hari lagi`,
      tone: "warn",
      icon: "mdi-clock-alert-outline",
    };

  return {
    text: `${days} hari lagi`,
    tone: "muted",
    icon: "mdi-clock-outline",
  };
});

// One icon and tone per stored status, so a cancelled activity does not wear
// the same calendar-check as a completed one. Finished stays neutral: spec 001
// criterion 14 requires it, because finished is a fact and not an outcome.
const STATUS_MARKERS: Record<
  ActivityStatus,
  { tone: StatusMarker["tone"]; icon: string }
> = {
  ON_REGISTRATION: { tone: "muted", icon: "mdi-clock-outline" },
  CLOSED_REGISTRATION: { tone: "muted", icon: "mdi-calendar-remove-outline" },
  ON_PROGRESS: { tone: "muted", icon: "mdi-progress-clock" },
  FINISHED: { tone: "muted", icon: "mdi-calendar-check-outline" },
  CANCELLED: { tone: "danger", icon: "mdi-close-circle-outline" },
};

// The countdown belongs to an activity that is still taking registrations.
// Every other status states itself instead, and never reads
// registrationEndStatus, so no countdown is computed for it.
const statusMarker = computed<StatusMarker>(() => {
  if (!isFinished.value) return registrationEndStatus.value;
  const marker = STATUS_MARKERS[props.contentStatus];
  return {
    text: ACTIVITY_STATUS_LABEL[props.contentStatus],
    tone: marker.tone,
    icon: marker.icon,
  };
});

// A banner that already failed before hydration never emits `error`.
const imageElement = ref<HTMLImageElement | null>(null);
const setImageElement = (
  element: Element | ComponentPublicInstance | null,
): void => {
  imageElement.value = element instanceof HTMLImageElement ? element : null;
};
onMounted(() => {
  const image = imageElement.value;
  if (image?.complete && image.naturalWidth === 0) hasImageError.value = true;
});
</script>

<style scoped>
.gr-listcard {
  display: flex;
  flex-direction: column;
  height: 100%;
  border: 1px solid var(--gr-line);
  border-radius: 4px;
  background-color: var(--gr-surface);
  color: inherit;
  text-decoration: none;
  transition:
    border-color 180ms cubic-bezier(0.2, 0, 0, 1),
    background-color 180ms cubic-bezier(0.2, 0, 0, 1);
}

@media (hover: hover) {
  .gr-listcard:hover {
    border-color: var(--gr-green-deep);
  }

  .gr-listcard:hover .gr-listcard__title {
    text-decoration: underline;
    text-underline-offset: 3px;
  }
}

.gr-listcard__media {
  aspect-ratio: 16 / 9;
  overflow: hidden;
  border-radius: 4px 4px 0 0;
  background-color: var(--gr-green-tint);
}

.gr-listcard__img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gr-listcard__img--fallback {
  display: grid;
  place-items: center;
  color: var(--gr-ink-3);
}

.gr-listcard__body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  padding: 16px;
}

.gr-listcard__title {
  display: -webkit-box;
  margin: 0;
  color: var(--gr-ink);
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.4;
  /* Two lines reserved in em so the box grows with zoom instead of clipping. */
  min-height: 2.8em;
  line-clamp: 2;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gr-listcard__date {
  margin: 8px 0 0;
  color: var(--gr-ink-3);
  font-size: 0.8125rem;
  line-height: 1.4;
}

.gr-listcard__date--lead {
  margin: 0 0 6px;
  color: var(--gr-ink-2);
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.gr-listcard__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 12px;
}

.gr-listcard__status,
.gr-listcard__city {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  font-size: 0.8125rem;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.gr-listcard__city {
  color: var(--gr-ink-3);
}

.gr-listcard__status--muted {
  color: var(--gr-ink-3);
}

/* Vuetify's `warning` is #fb8c00, which measures 2.2:1 on white and misses the
   4.5:1 spec 001 criterion 27 owes this text. Both tones below are the same
   semantic colours one step darker. */
.gr-listcard__status--warn {
  color: var(--gr-warn);
  font-weight: 700;
}

.gr-listcard__status--danger {
  color: var(--gr-danger);
  font-weight: 700;
}

/* Two-up on a phone leaves each card about 165px wide, so the padding and the
   supporting rows step down and the meta row stacks instead of colliding. */
@media (max-width: 599px) {
  .gr-listcard__body {
    padding: 12px;
  }

  .gr-listcard__title {
    font-size: 0.9375rem;
  }

  .gr-listcard__date {
    margin-top: 6px;
    font-size: 0.75rem;
  }

  .gr-listcard__date--lead {
    font-size: 0.6875rem;
  }

  .gr-listcard__meta {
    flex-direction: column;
    gap: 4px;
    padding-top: 10px;
  }

  .gr-listcard__status,
  .gr-listcard__city {
    font-size: 0.75rem;
  }
}
</style>
